import QRCode from 'qrcode.react';
import {
  ActivationPackWithTarget,
  GeneratedAsset,
  ASSET_TYPE_INFO,
} from '@/types/activation/activation-pack';
import { ActivationPackService } from '@/services/database/ActivationPackService';
import { GenieBranding, QRDisplayConfig, DEFAULT_QR_DISPLAY_CONFIG } from '@/types/ui/branding';
import { APP_STORE_BADGE_SRC, GOOGLE_PLAY_BADGE_SRC } from '@/constants/appStore';
import { generateLookbookPdf as buildLookbookPdf } from '@/services/activation/lookbookPdf';
import { generatePosterPackPdf as buildPosterPackPdf } from '@/services/activation/posterPdf';
import { buildEmbedPath } from '@/lib/embedUrl';
import { productManualUrl } from '@/services/marketplace/ProductGenieService';

const escapeHtml = (value: string): string =>
  value.replace(/[&<>'"]/g, (character) => {
    const entities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;',
    };
    return entities[character];
  });

const requireSafeHttpUrl = (value: string, label: string): string => {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      throw new Error('Unsupported protocol');
    }
    return url.toString();
  } catch {
    throw new Error(`${label} must be a valid HTTP or HTTPS URL`);
  }
};

const optionalSafeHttpUrl = (value?: string | null): string | null => {
  if (!value) return null;
  try {
    return requireSafeHttpUrl(value, 'Logo URL');
  } catch {
    return null;
  }
};

const safeEmailColor = (value: string | null | undefined, fallback: string): string =>
  value && /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(value) ? value : fallback;

// A4 portrait at 300 DPI and the matching jsPDF point size.
const A4_PX = { w: 2480, h: 3508 };
const A4_PT = { w: 595.28, h: 841.89 };
const A4_LAYOUT_SCALE = 2;

/**
 * Service for generating downloadable assets from activation packs
 */
export class AssetGeneratorService {
  // =====================
  // QR Code Generation
  // =====================

  static async generateQRPng(
    pack: ActivationPackWithTarget,
    displayConfig?: Partial<QRDisplayConfig>
  ): Promise<GeneratedAsset> {
    const url = ActivationPackService.getTargetUrl(pack);
    const branding = ActivationPackService.getEffectiveBranding(pack);
    const name = ActivationPackService.getTargetName(pack);

    const config: QRDisplayConfig = {
      ...DEFAULT_QR_DISPLAY_CONFIG,
      ...displayConfig,
      style: pack.asset_config.qr_style || 'framed',
      label: pack.asset_config.qr_label || 'scan-to-connect',
      showLogo: pack.asset_config.qr_include_logo ?? true,
    };

    const canvas = await this.generateQRCanvas(url, name, branding, config);
    const dataUrl = canvas.toDataURL('image/png');

    return {
      type: 'qr_png',
      label: ASSET_TYPE_INFO.qr_png.label,
      description: ASSET_TYPE_INFO.qr_png.description,
      data: dataUrl,
      filename: `${this.sanitizeFilename(pack.name)}-qr-code.png`,
      mimeType: 'image/png',
    };
  }

  static async generateQRSvg(
    pack: ActivationPackWithTarget,
    displayConfig?: Partial<QRDisplayConfig>
  ): Promise<GeneratedAsset> {
    const url = ActivationPackService.getTargetUrl(pack);
    const branding = ActivationPackService.getEffectiveBranding(pack);
    const name = ActivationPackService.getTargetName(pack);

    const config: QRDisplayConfig = {
      ...DEFAULT_QR_DISPLAY_CONFIG,
      ...displayConfig,
      style: pack.asset_config.qr_style || 'framed',
      label: pack.asset_config.qr_label || 'scan-to-connect',
      showLogo: pack.asset_config.qr_include_logo ?? true,
    };

    const svg = await this.generateQRSvgContent(url, name, branding, config);

    return {
      type: 'qr_svg',
      label: ASSET_TYPE_INFO.qr_svg.label,
      description: ASSET_TYPE_INFO.qr_svg.description,
      data: svg,
      filename: `${this.sanitizeFilename(pack.name)}-qr-code.svg`,
      mimeType: 'image/svg+xml',
    };
  }

  // =====================
  // Product Manual QR (Manual 2.0)
  // =====================

  /** The product the pack's QR opens; throws when the pack has none so a caller never ships a blank code. */
  private static productTarget(pack: ActivationPackWithTarget): { url: string; name: string } {
    const slug = pack.asset_config.product_slug;
    if (!slug) throw new Error('This activation pack has no product chosen for a product manual QR');
    return {
      url: productManualUrl(slug),
      name: pack.asset_config.product_name || ActivationPackService.getTargetName(pack),
    };
  }

  private static productQRConfig(pack: ActivationPackWithTarget, displayConfig?: Partial<QRDisplayConfig>): QRDisplayConfig {
    return {
      ...DEFAULT_QR_DISPLAY_CONFIG,
      ...displayConfig,
      style: pack.asset_config.qr_style || 'framed',
      label: pack.asset_config.qr_label || 'scan-to-connect',
      showLogo: pack.asset_config.qr_include_logo ?? true,
    };
  }

  static async generateProductQRPng(
    pack: ActivationPackWithTarget,
    displayConfig?: Partial<QRDisplayConfig>
  ): Promise<GeneratedAsset> {
    const { url, name } = this.productTarget(pack);
    const branding = ActivationPackService.getEffectiveBranding(pack);
    const canvas = await this.generateQRCanvas(url, name, branding, this.productQRConfig(pack, displayConfig));

    return {
      type: 'product_qr_png',
      label: ASSET_TYPE_INFO.product_qr_png.label,
      description: ASSET_TYPE_INFO.product_qr_png.description,
      data: canvas.toDataURL('image/png'),
      filename: `${this.sanitizeFilename(name)}-manual-qr-code.png`,
      mimeType: 'image/png',
    };
  }

  static async generateProductQRSvg(
    pack: ActivationPackWithTarget,
    displayConfig?: Partial<QRDisplayConfig>
  ): Promise<GeneratedAsset> {
    const { url, name } = this.productTarget(pack);
    const branding = ActivationPackService.getEffectiveBranding(pack);
    const svg = await this.generateQRSvgContent(url, name, branding, this.productQRConfig(pack, displayConfig));

    return {
      type: 'product_qr_svg',
      label: ASSET_TYPE_INFO.product_qr_svg.label,
      description: ASSET_TYPE_INFO.product_qr_svg.description,
      data: svg,
      filename: `${this.sanitizeFilename(name)}-manual-qr-code.svg`,
      mimeType: 'image/svg+xml',
    };
  }

  // =====================
  // Email Signature
  // =====================

  static generateEmailSignature(pack: ActivationPackWithTarget): GeneratedAsset {
    const branding = ActivationPackService.getEffectiveBranding(pack);
    const isPortal = ActivationPackService.getTargetType(pack) === 'portal';
    const name = escapeHtml(ActivationPackService.getTargetName(pack));
    const talkUrl = escapeHtml(
      requireSafeHttpUrl(ActivationPackService.getTargetUrl(pack), 'Genie link'),
    );
    const appUrl = escapeHtml(
      requireSafeHttpUrl(
        ActivationPackService.getAppSmartLinkUrl(pack, 'email-signature'),
        'App link',
      ),
    );
    const description = escapeHtml(
      (pack.asset_config.tagline || (
        isPortal
          ? 'Open our help portal to find the right place to start.'
          : 'Start a voice or chat conversation.'
      ))
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 160),
    );
    const logoUrl = optionalSafeHttpUrl(branding.logoUrl);
    const primaryColor = safeEmailColor(branding.primaryColor, '#4E9CFF');
    const accentColor = safeEmailColor(branding.secondaryColor, '#FF4DB8');

    // Table layout and inline styles are deliberate: Gmail and Outlook strip
    // much of the CSS that would be available to a normal web component.
    const html = `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-family:Poppins,Arial,Helvetica,sans-serif;color:#111827;max-width:520px;">
  <tr>
    <td style="padding:14px 16px;border:1px solid #E5E7EB;border-left:5px solid ${primaryColor};border-radius:10px;background-color:#FFFFFF;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;width:100%;">
        <tr>
          ${logoUrl ? `
          <td width="60" style="width:60px;padding-right:14px;vertical-align:top;">
            <img src="${escapeHtml(logoUrl)}" alt="${name}" width="56" height="56" style="display:block;width:56px;height:56px;border:0;border-radius:12px;object-fit:cover;" />
          </td>
          ` : ''}
          <td style="vertical-align:top;">
            <div style="font-family:Poppins,Arial,Helvetica,sans-serif;font-size:17px;line-height:22px;font-weight:700;color:#111827;">${isPortal ? 'Explore' : 'Meet'} ${name}</div>
            <div style="padding-top:3px;font-family:Poppins,Arial,Helvetica,sans-serif;font-size:13px;line-height:19px;color:#4B5563;">${description}</div>
          </td>
        </tr>
        <tr>
          <td ${logoUrl ? 'colspan="2"' : ''} style="padding-top:13px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
              <tr>
                <td style="padding-right:8px;">
                  <a href="${talkUrl}" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:9px 14px;border-radius:7px;background-color:${primaryColor};font-family:Poppins,Arial,Helvetica,sans-serif;font-size:13px;line-height:18px;font-weight:700;color:#FFFFFF;text-decoration:none;">${isPortal ? 'Open' : 'Talk to'} ${name}</a>
                </td>
                <td>
                  <a href="${appUrl}" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:8px 13px;border:1px solid ${accentColor};border-radius:7px;background-color:#FFFFFF;font-family:Poppins,Arial,Helvetica,sans-serif;font-size:13px;line-height:18px;font-weight:700;color:${accentColor};text-decoration:none;">Get the app</a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
`.trim();

    return {
      type: 'email_signature_html',
      label: ASSET_TYPE_INFO.email_signature_html.label,
      description: ASSET_TYPE_INFO.email_signature_html.description,
      data: html,
      filename: `${this.sanitizeFilename(pack.name)}-email-signature.html`,
      mimeType: 'text/html',
    };
  }

  // =====================
  // Embed Code
  // =====================

  static generateEmbedCode(pack: ActivationPackWithTarget): GeneratedAsset {
    const targetUrl = requireSafeHttpUrl(
      ActivationPackService.getTargetUrl(pack),
      'Genie embed link',
    );
    const canonicalTarget = new URL(targetUrl);
    const agentUrlName =
      pack.agent && ((pack.agent as { url_name?: string }).url_name ?? pack.agent.page?.url_name);

    // Portals do not have a separate embed route. Their public `/portal/:slug`
    // page is already iframe-compatible, whereas Genies embed via the live agent page.
    if (!canonicalTarget.pathname.startsWith('/portal/') && agentUrlName) {
      const embedPath = buildEmbedPath(agentUrlName);
      const [pathname, search] = embedPath.split('?');
      canonicalTarget.pathname = pathname;
      canonicalTarget.search = search ? `?${search}` : '';
      canonicalTarget.hash = '';
    }

    const embedUrl = escapeHtml(canonicalTarget.toString());
    const safePackName = escapeHtml(pack.name);
    const html = `<!-- ${safePackName} Widget -->
<iframe
  src="${embedUrl}"
  title="${safePackName}"
  width="100%"
  height="600"
  frameborder="0"
  allow="microphone; autoplay"
  style="border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); max-width: 500px;"
></iframe>`;

    return {
      type: 'embed_code',
      label: ASSET_TYPE_INFO.embed_code.label,
      description: ASSET_TYPE_INFO.embed_code.description,
      data: html,
      filename: `${this.sanitizeFilename(pack.name)}-embed-code.html`,
      mimeType: 'text/html',
    };
  }

  // =====================
  // Social Card (1080x1080)
  // =====================

  static async generateSocialCard(pack: ActivationPackWithTarget): Promise<GeneratedAsset> {
    const branding = ActivationPackService.getEffectiveBranding(pack);
    const name = ActivationPackService.getTargetName(pack);
    const isPortal = ActivationPackService.getTargetType(pack) === 'portal';
    const tagline = pack.asset_config.tagline || (
      isPortal ? 'Scan to open our help portal' : 'Scan to talk to our assistant'
    );

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const size = 1080;

    canvas.width = size;
    canvas.height = size;

    // Draw gradient background
    const primaryColor = branding.primaryColor || '#4E9CFF';
    const secondaryColor = branding.secondaryColor || primaryColor;

    const gradient = ctx.createLinearGradient(0, 0, size, size);
    gradient.addColorStop(0, primaryColor);
    gradient.addColorStop(1, secondaryColor);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);

    // Draw white card area
    const cardPadding = 80;
    const cardRadius = 40;
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.roundRect(cardPadding, cardPadding, size - cardPadding * 2, size - cardPadding * 2, cardRadius);
    ctx.fill();

    // Draw name
    ctx.fillStyle = '#1a1a1a';
    ctx.font = 'bold 64px Poppins, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(name, size / 2, 220);

    // Draw tagline
    ctx.fillStyle = '#666666';
    ctx.font = '32px Poppins, system-ui, sans-serif';
    ctx.fillText(tagline, size / 2, 280);

    // Generate and draw QR code in center
    const url = ActivationPackService.getTargetUrl(pack);
    const qrSize = 400;

    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-10000px';
    document.body.appendChild(tempDiv);

    const { createRoot } = await import('react-dom/client');
    const root = createRoot(tempDiv);

    const QRElement = QRCode({
      value: url,
      size: qrSize,
      fgColor: '#000000',
      bgColor: '#FFFFFF',
      level: 'H',
      includeMargin: false,
    });

    root.render(QRElement);
    await new Promise((resolve) => setTimeout(resolve, 200));

    const qrCanvas = tempDiv.querySelector('canvas');
    if (qrCanvas) {
      const qrX = (size - qrSize) / 2;
      const qrY = 340;
      ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);
    }

    root.unmount();
    document.body.removeChild(tempDiv);

    // Draw CTA text at bottom
    ctx.fillStyle = primaryColor;
    ctx.font = 'bold 36px Poppins, system-ui, sans-serif';
    ctx.fillText(isPortal ? 'Scan to Open' : 'Scan to Connect', size / 2, 820);

    // Draw logo if available
    if (branding.logoUrl) {
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        await new Promise<void>((resolve) => {
          logoImg.onload = () => {
            const logoSize = 60;
            ctx.drawImage(logoImg, (size - qrSize) / 2 + qrSize / 2 - logoSize / 2, 340 + qrSize / 2 - logoSize / 2, logoSize, logoSize);
            resolve();
          };
          logoImg.onerror = () => resolve();
          logoImg.src = branding.logoUrl!;
          setTimeout(() => resolve(), 2000);
        });
      } catch (e) {
        console.warn('Failed to load logo for social card');
      }
    }

    const dataUrl = canvas.toDataURL('image/png');

    return {
      type: 'social_card_png',
      label: ASSET_TYPE_INFO.social_card_png.label,
      description: ASSET_TYPE_INFO.social_card_png.description,
      data: dataUrl,
      filename: `${this.sanitizeFilename(pack.name)}-social-card.png`,
      mimeType: 'image/png',
    };
  }

  // =====================
  // Sticker PDF (Print-Ready)
  // =====================

  static async generateStickerPdf(pack: ActivationPackWithTarget): Promise<GeneratedAsset> {
    // Dynamic import jsPDF
    const { default: jsPDF } = await import('jspdf');

    const branding = ActivationPackService.getEffectiveBranding(pack);
    const name = ActivationPackService.getTargetName(pack);
    const url = ActivationPackService.getTargetUrl(pack);

    // Get sticker size in inches
    const stickerSizeMap: Record<string, number> = {
      '2x2': 2,
      '3x3': 3,
      '4x4': 4,
    };
    const stickerSize = stickerSizeMap[pack.asset_config.sticker_size || '3x3'] || 3;
    const dpi = 300;
    const pixelSize = stickerSize * dpi;

    // Create PDF (dimensions in mm)
    const sizeInMm = stickerSize * 25.4;
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [sizeInMm, sizeInMm],
      compress: true,
    });

    // Generate QR canvas
    const qrCanvas = await this.generateQRCanvas(url, name, branding, {
      style: 'minimal',
      label: 'none',
      transparentBackground: false,
      showLogo: pack.asset_config.qr_include_logo ?? true,
      foregroundColor: '#000000',
      backgroundColor: '#FFFFFF',
    }, pixelSize * 0.8);

    // Add QR to PDF
    const qrDataUrl = qrCanvas.toDataURL('image/png');
    const margin = sizeInMm * 0.1;
    const qrSizeInPdf = sizeInMm - margin * 2;

    pdf.addImage(qrDataUrl, 'PNG', margin, margin, qrSizeInPdf, qrSizeInPdf, undefined, 'FAST');

    // Add tagline at bottom
    const tagline = pack.asset_config.tagline || 'Scan to Connect';
    pdf.setFontSize(sizeInMm * 0.08);
    pdf.setTextColor(branding.primaryColor || '#4E9CFF');
    pdf.text(tagline, sizeInMm / 2, sizeInMm - margin / 2, { align: 'center' });

    const pdfBlob = pdf.output('blob');

    return {
      type: 'sticker_pdf',
      label: ASSET_TYPE_INFO.sticker_pdf.label,
      description: `${stickerSize}x${stickerSize} inch print-ready sticker`,
      data: pdfBlob,
      filename: `${this.sanitizeFilename(pack.name)}-sticker-${stickerSize}x${stickerSize}.pdf`,
      mimeType: 'application/pdf',
    };
  }

  // =====================
  // App Install Poster (Smart Link)
  // =====================

  /**
   * A branded "scan to get the app" poster. The QR points at the device-aware
   * smart link, so the same poster works on phone (→ App/Play store) and
   * desktop (→ web Genie). `png` for screens/socials, `pdf` for the printer.
   */
  static async generateAppInstallPoster(
    pack: ActivationPackWithTarget,
    format: 'png' | 'pdf' = 'png',
  ): Promise<GeneratedAsset> {
    const branding = ActivationPackService.getEffectiveBranding(pack);
    const name = ActivationPackService.getTargetName(pack);
    const smartUrl = ActivationPackService.getAppSmartLinkUrl(pack);

    const W = A4_PX.w / A4_LAYOUT_SCALE;
    const H = A4_PX.h / A4_LAYOUT_SCALE;
    const canvas = document.createElement('canvas');
    canvas.width = A4_PX.w;
    canvas.height = A4_PX.h;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(A4_LAYOUT_SCALE, A4_LAYOUT_SCALE);

    this.paintBrandBackground(ctx, W, H, branding);
    ctx.textAlign = 'center';

    const cx = W / 2;
    let y = 210;

    // Logo tile (or skip gracefully)
    if (branding.logoUrl) {
      const logo = await this.loadImage(branding.logoUrl);
      if (logo) {
        const s = 150;
        ctx.save();
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.roundRect(cx - s / 2 - 18, y - 18, s + 36, s + 36, 28);
        ctx.fill();
        ctx.restore();
        ctx.drawImage(logo, cx - s / 2, y, s, s);
        y += s + 70;
      }
    }

    // Headline — drawCenteredWrapped returns the LAST line's baseline, so the
    // subhead must be offset from that (not a fixed delta) to clear a 2-line wrap.
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '700 74px Poppins, system-ui, sans-serif';
    y = this.drawCenteredWrapped(ctx, `Get ${name} on your phone`, cx, y + 70, W - 200, 86);

    // Subhead — generous gap below the (possibly two-line) headline
    ctx.font = '500 38px Poppins, system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    y += 80;
    ctx.fillText('Scan to get started', cx, y);
    y += 88;

    // QR on a white rounded card (transparent QR bg so the card corners show)
    const qrCanvas = await this.generateQRCanvas(smartUrl, name, branding, {
      style: 'card',
      label: 'none',
      transparentBackground: true,
      showLogo: true,
      foregroundColor: '#000000',
      backgroundColor: '#FFFFFF',
    }, 600 * A4_LAYOUT_SCALE);
    const qrW = 660;
    const qrH = qrW * (qrCanvas.height / qrCanvas.width);
    const qrX = cx - qrW / 2;
    ctx.save();
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.roundRect(qrX - 24, y - 24, qrW + 48, qrH + 48, 36);
    ctx.fill();
    ctx.restore();
    ctx.drawImage(qrCanvas, qrX, y, qrW, qrH);
    y += qrH + 96;

    // Store badges + footer
    await this.drawStoreBadges(ctx, cx, y, 72);
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.font = '600 30px Poppins, system-ui, sans-serif';
    ctx.fillText('Powered by Help Genie', cx, H - 72);

    const pngDataUrl = canvas.toDataURL('image/png');

    if (format === 'pdf') {
      const { default: jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
      pdf.addImage(pngDataUrl, 'PNG', 0, 0, A4_PT.w, A4_PT.h, undefined, 'FAST');
      return {
        type: 'app_poster_pdf',
        label: ASSET_TYPE_INFO.app_poster_pdf.label,
        description: ASSET_TYPE_INFO.app_poster_pdf.description,
        data: pdf.output('blob'),
        filename: `${this.sanitizeFilename(pack.name)}-app-poster.pdf`,
        mimeType: 'application/pdf',
      };
    }

    return {
      type: 'app_poster_png',
      label: ASSET_TYPE_INFO.app_poster_png.label,
      description: ASSET_TYPE_INFO.app_poster_png.description,
      data: pngDataUrl,
      filename: `${this.sanitizeFilename(pack.name)}-app-poster.png`,
      mimeType: 'image/png',
    };
  }

  // =====================
  // Lookbook PDF (multi-page)
  // =====================

  /**
   * A branded, multi-page lookbook PDF showing the Genie out in the wild —
   * cover, a designed placement page per physical surface (each with a scannable
   * branded QR), and a "how it works" closing page. Rendered from the SAME React
   * components shown in the in-app gallery, rasterized via html2canvas, so the
   * preview and the download stay pixel-identical. See `lookbookPdf.ts`.
   */
  static generateLookbookPdf(pack: ActivationPackWithTarget): Promise<GeneratedAsset> {
    return buildLookbookPdf(pack);
  }

  // =====================
  // Poster Pack PDF (multi-page)
  // =====================

  /**
   * A branded, multi-page PDF of standalone poster designs — each page IS a
   * full print-ready poster (not a mockup), rendered from the SAME React
   * components shown in the in-app gallery. See `posterPdf.ts`.
   */
  static generatePosterPackPdf(pack: ActivationPackWithTarget): Promise<GeneratedAsset> {
    return buildPosterPackPdf(pack);
  }

  // =====================
  // Batch Generation
  // =====================

  static async generateAllAssets(pack: ActivationPackWithTarget): Promise<GeneratedAsset[]> {
    const assets: GeneratedAsset[] = [];
    const touchpoints = pack.touchpoints;

    // Always generate QR codes
    assets.push(await this.generateQRPng(pack));
    assets.push(await this.generateQRSvg(pack));

    // A pack pointed at a catalogue product also gets that product's manual QR.
    if (pack.asset_config.product_slug) {
      assets.push(await this.generateProductQRPng(pack));
      assets.push(await this.generateProductQRSvg(pack));
    }

    // Conditional assets based on touchpoints
    if (touchpoints.qr_sticker) {
      assets.push(await this.generateStickerPdf(pack));
    }

    if (touchpoints.email_signature) {
      assets.push(this.generateEmailSignature(pack));
    }

    if (touchpoints.social_card) {
      assets.push(await this.generateSocialCard(pack));
    }

    // Always include embed code
    assets.push(this.generateEmbedCode(pack));

    // App posters are cheap canvas work — include them in the batch. The
    // lookbook PDF is heavy (html2canvas) so it's generated on demand instead
    // (see useAssetGenerator.downloadAllAssets / the Lookbook section), keeping
    // the silent first-visit pre-build snappy.
    try {
      assets.push(await this.generateAppInstallPoster(pack, 'png'));
    } catch (e) {
      console.error('Failed to generate app poster (png):', e);
    }
    try {
      assets.push(await this.generateAppInstallPoster(pack, 'pdf'));
    } catch (e) {
      console.error('Failed to generate app poster (pdf):', e);
    }

    return assets;
  }

  // =====================
  // Private Helpers
  // =====================

  private static async generateQRCanvas(
    url: string,
    name: string,
    branding: Partial<GenieBranding>,
    config: QRDisplayConfig,
    size = 400
  ): Promise<HTMLCanvasElement> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    const primaryColor = branding.primaryColor || '#4E9CFF';
    const secondaryColor = branding.secondaryColor || primaryColor;
    const qrForegroundColor = config.foregroundColor || '#000000';
    const qrBackgroundColor = config.backgroundColor || '#FFFFFF';

    // Calculate dimensions
    let frameSize: number;
    let labelHeight = 0;

    if (config.style === 'minimal') {
      frameSize = size + 32;
    } else if (config.style === 'framed') {
      frameSize = size + 120;
      labelHeight = config.label !== 'none' ? 50 : 0;
    } else {
      frameSize = size + 48;
      labelHeight = config.label !== 'none' ? 40 : 0;
    }

    canvas.width = frameSize;
    canvas.height = frameSize + labelHeight;

    // Draw background
    if (!config.transparentBackground) {
      if (config.style === 'framed') {
        if (branding.gradientEnabled) {
          const gradient = ctx.createLinearGradient(0, 0, frameSize, frameSize);
          gradient.addColorStop(0, primaryColor);
          gradient.addColorStop(1, secondaryColor);
          ctx.fillStyle = gradient;
        } else {
          ctx.fillStyle = primaryColor;
        }
        ctx.beginPath();
        ctx.roundRect(0, 0, frameSize, frameSize + labelHeight, 20);
        ctx.fill();
      } else {
        ctx.fillStyle = qrBackgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }

    // Generate QR code
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-10000px';
    document.body.appendChild(tempDiv);

    const { createRoot } = await import('react-dom/client');
    const root = createRoot(tempDiv);

    const QRElement = QRCode({
      value: url,
      size,
      fgColor: qrForegroundColor,
      bgColor: config.transparentBackground ? 'transparent' : qrBackgroundColor,
      level: 'H',
      includeMargin: false,
    });

    root.render(QRElement);
    await new Promise((resolve) => setTimeout(resolve, 200));

    const qrCanvas = tempDiv.querySelector('canvas');
    if (qrCanvas) {
      const qrX = (frameSize - size) / 2;
      const qrY = config.style === 'framed' ? (frameSize - size) / 2 : 24;

      if (config.style === 'framed') {
        // Draw white container for QR
        ctx.fillStyle = qrBackgroundColor;
        ctx.beginPath();
        ctx.roundRect(qrX - 20, qrY - 20, size + 40, size + 40, 16);
        ctx.fill();
      }

      ctx.drawImage(qrCanvas, qrX, qrY, size, size);
    }

    root.unmount();
    document.body.removeChild(tempDiv);

    // Add logo if configured
    if (config.showLogo && branding.logoUrl) {
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        await new Promise<void>((resolve) => {
          logoImg.onload = () => {
            const logoSize = size * 0.15;
            const qrX = (frameSize - size) / 2;
            const qrY = config.style === 'framed' ? (frameSize - size) / 2 : 24;
            const logoX = qrX + size / 2 - logoSize / 2;
            const logoY = qrY + size / 2 - logoSize / 2;

            ctx.fillStyle = qrBackgroundColor;
            ctx.fillRect(logoX - 5, logoY - 5, logoSize + 10, logoSize + 10);
            ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
            resolve();
          };
          logoImg.onerror = () => resolve();
          logoImg.src = branding.logoUrl!;
          setTimeout(() => resolve(), 2000);
        });
      } catch (e) {
        console.warn('Failed to load logo');
      }
    }

    return canvas;
  }

  private static async generateQRSvgContent(
    url: string,
    name: string,
    branding: Partial<GenieBranding>,
    config: QRDisplayConfig,
    size = 400
  ): Promise<string> {
    const primaryColor = branding.primaryColor || '#4E9CFF';
    const qrForegroundColor = config.foregroundColor || '#000000';
    const qrBackgroundColor = config.backgroundColor || '#FFFFFF';

    // Generate QR as SVG
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-10000px';
    document.body.appendChild(tempDiv);

    const { createRoot } = await import('react-dom/client');
    const root = createRoot(tempDiv);

    const QRElement = QRCode({
      value: url,
      size,
      fgColor: qrForegroundColor,
      bgColor: config.transparentBackground ? 'transparent' : qrBackgroundColor,
      level: 'H',
      includeMargin: false,
      renderAs: 'svg',
    });

    root.render(QRElement);
    await new Promise((resolve) => setTimeout(resolve, 200));

    const qrSvg = tempDiv.querySelector('svg');
    const qrSvgContent = qrSvg ? qrSvg.innerHTML : '';

    root.unmount();
    document.body.removeChild(tempDiv);

    // Build complete SVG
    const frameSize = size + 48;

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${frameSize}" height="${frameSize}" viewBox="0 0 ${frameSize} ${frameSize}">
      <rect width="${frameSize}" height="${frameSize}" rx="16" fill="${qrBackgroundColor}"/>
      <g transform="translate(24, 24)">${qrSvgContent}</g>
    </svg>`;
  }

  /** Paint the signature brand gradient + soft radial glows onto a canvas. */
  private static paintBrandBackground(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    branding: Partial<GenieBranding>,
  ): void {
    const primary = branding.primaryColor || '#4E9CFF';
    const secondary = branding.secondaryColor || '#FF4DB8';

    const gradient = ctx.createLinearGradient(0, 0, w, h);
    gradient.addColorStop(0, primary);
    gradient.addColorStop(1, secondary);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);

    const blob = (cx: number, cy: number, r: number, color: string, alpha: number) => {
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
      g.addColorStop(0, color);
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    };
    blob(w * 0.18, h * 0.12, w * 0.55, '#FFFFFF', 0.18);
    blob(w * 0.86, h * 0.82, w * 0.6, secondary, 0.22);
  }

  /** Load an image with CORS + a timeout; resolves null on any failure. */
  private static loadImage(src: string, timeoutMs = 3000): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
      if (!src) {
        resolve(null);
        return;
      }
      let settled = false;
      const finish = (img: HTMLImageElement | null) => {
        if (settled) return;
        settled = true;
        resolve(img);
      };
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => finish(img);
      img.onerror = () => finish(null);
      img.src = src;
      setTimeout(() => finish(null), timeoutMs);
    });
  }

  /**
   * Draw centered, word-wrapped text. `y` is the baseline of the first line;
   * returns the baseline y of the last line drawn.
   */
  private static drawCenteredWrapped(
    ctx: CanvasRenderingContext2D,
    text: string,
    cx: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
  ): number {
    const words = String(text).split(/\s+/).filter(Boolean);
    let line = '';
    let curY = y;
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        ctx.fillText(line, cx, curY);
        line = word;
        curY += lineHeight;
      } else {
        line = test;
      }
    }
    if (line) ctx.fillText(line, cx, curY);
    return curY;
  }

  /** Draw App Store + Google Play badges centered, with a text fallback. */
  private static async drawStoreBadges(
    ctx: CanvasRenderingContext2D,
    cx: number,
    y: number,
    badgeH: number,
  ): Promise<void> {
    const gap = 30;
    const [apple, google] = await Promise.all([
      this.loadImage(APP_STORE_BADGE_SRC),
      this.loadImage(GOOGLE_PLAY_BADGE_SRC),
    ]);
    const items: { img: HTMLImageElement; w: number }[] = [];
    if (apple && apple.width && apple.height) {
      items.push({ img: apple, w: badgeH * (apple.width / apple.height) });
    }
    if (google && google.width && google.height) {
      items.push({ img: google, w: badgeH * (google.width / google.height) });
    }

    if (items.length > 0) {
      const totalW = items.reduce((s, it) => s + it.w, 0) + gap * (items.length - 1);
      let x = cx - totalW / 2;
      for (const it of items) {
        ctx.drawImage(it.img, x, y, it.w, badgeH);
        x += it.w + gap;
      }
    } else {
      ctx.save();
      ctx.textAlign = 'center';
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '600 34px Poppins, system-ui, sans-serif';
      ctx.fillText('App Store  ·  Google Play', cx, y + badgeH / 2);
      ctx.restore();
    }
  }

  private static sanitizeFilename(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }
}
